import { createContext, ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TeamChannelValue } from './team-channel-section.type';
import { ValidationError, ValidationRule } from './validators';

interface TeamChannelContextValue {
  getTeamError: (teamId: string) => ValidationError | undefined;
  getChannelError: (channelId: string) => ValidationError | undefined;
  updateTeamMax: (teamId: string, max: number | null) => void;
  updateChannelMax: (channelId: string, max: number | null) => void;
  setTeamSalesClosed: (teamId: string, isClosed: boolean) => void;
  setChannelSalesClosed: (channelId: string, isClosed: boolean) => void;
}

const TeamChannelContext = createContext<TeamChannelContextValue | undefined>(undefined);

export const useTeamChannelContext = (): TeamChannelContextValue => {
  const ctx = useContext(TeamChannelContext);
  if (ctx === undefined) {
    throw new Error('useTeamChannelContext must be used inside <TeamChannelSection>');
  }
  return ctx;
};

// Highest priority first.
const RULE_PRIORITY: ValidationRule[] = ['negative', 'over-capacity', 'over-team-limit', 'over-channel-capacity'];

const RULE_TO_KEY: Record<ValidationRule, string> = {
  negative: 'offers:channelMaxQuantityNegativeError',
  'over-capacity': 'offers:teamMaxQuantityHigherThanEventCapacityError',
  'over-team-limit': 'offers:channelMaxQuantityHigherThanTeamCapacityError',
  'over-channel-capacity': 'offers:channelMaxQuantityHigherThanEventCapacityError',
};

const pickByPriority = (candidates: ValidationError[]): ValidationError | undefined => {
  if (candidates.length === 0) return undefined;
  for (const rule of RULE_PRIORITY) {
    const hit = candidates.find((e) => e.rule === rule);
    if (hit) return hit;
  }
  return candidates[0];
};

export const useTeamChannelErrorMessage = (error: ValidationError | undefined): string | undefined => {
  const { t } = useTranslation();
  if (!error) return undefined;
  if (error.message) return error.message;
  return t(RULE_TO_KEY[error.rule], { min: 1 });
};

interface ProviderProps {
  value: TeamChannelValue;
  onChange: (next: TeamChannelValue) => void;
  errors?: ValidationError[];
  children: ReactNode;
}

export const TeamChannelProvider = ({ value, onChange, errors, children }: ProviderProps) => {
  // Keep refs synced with the latest value/onChange so the handlers below can stay stable
  // across renders. useLayoutEffect ensures the refs are updated synchronously after commit,
  // before any user event can fire a handler.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  const errorsByTeamId = useMemo(() => {
    const map = new Map<string, ValidationError[]>();
    for (const error of errors ?? []) {
      if (error.teamId && !error.channelId) {
        const list = map.get(error.teamId) ?? [];
        list.push(error);
        map.set(error.teamId, list);
      }
    }
    return map;
  }, [errors]);

  const errorsByChannelId = useMemo(() => {
    const map = new Map<string, ValidationError[]>();
    for (const error of errors ?? []) {
      if (error.channelId) {
        const list = map.get(error.channelId) ?? [];
        list.push(error);
        map.set(error.channelId, list);
      }
    }
    return map;
  }, [errors]);

  const getTeamError = useCallback(
    (teamId: string) => pickByPriority(errorsByTeamId.get(teamId) ?? []),
    [errorsByTeamId]
  );

  const getChannelError = useCallback(
    (channelId: string) => pickByPriority(errorsByChannelId.get(channelId) ?? []),
    [errorsByChannelId]
  );

  const updateTeamMax = useCallback((teamId: string, max: number | null) => {
    const v = valueRef.current;
    onChangeRef.current({
      ...v,
      teams: v.teams.map((team) => (team.id === teamId ? { ...team, maxTicketQuantity: max } : team)),
    });
  }, []);

  const updateChannelMax = useCallback((channelId: string, max: number | null) => {
    const v = valueRef.current;
    onChangeRef.current({
      teams: v.teams.map((team) => ({
        ...team,
        channels: team.channels.map((channel) =>
          channel.channel.id === channelId ? { ...channel, maxTicketQuantity: max } : channel
        ),
      })),
      channelsWithoutTeams: v.channelsWithoutTeams.map((channel) =>
        channel.channel.id === channelId ? { ...channel, maxTicketQuantity: max } : channel
      ),
    });
  }, []);

  const setTeamSalesClosed = useCallback((teamId: string, isClosed: boolean) => {
    const v = valueRef.current;
    onChangeRef.current({
      ...v,
      teams: v.teams.map((team) =>
        team.id === teamId
          ? {
              ...team,
              channels: team.channels.map((channel) => ({ ...channel, isTicketSalesClosed: isClosed })),
            }
          : team
      ),
    });
  }, []);

  const setChannelSalesClosed = useCallback((channelId: string, isClosed: boolean) => {
    const v = valueRef.current;
    onChangeRef.current({
      teams: v.teams.map((team) => ({
        ...team,
        channels: team.channels.map((channel) =>
          channel.channel.id === channelId ? { ...channel, isTicketSalesClosed: isClosed } : channel
        ),
      })),
      channelsWithoutTeams: v.channelsWithoutTeams.map((channel) =>
        channel.channel.id === channelId ? { ...channel, isTicketSalesClosed: isClosed } : channel
      ),
    });
  }, []);

  const contextValue = useMemo<TeamChannelContextValue>(
    () => ({
      getTeamError,
      getChannelError,
      updateTeamMax,
      updateChannelMax,
      setTeamSalesClosed,
      setChannelSalesClosed,
    }),
    [getTeamError, getChannelError, updateTeamMax, updateChannelMax, setTeamSalesClosed, setChannelSalesClosed]
  );

  return <TeamChannelContext.Provider value={contextValue}>{children}</TeamChannelContext.Provider>;
};
